import { TokenResponse } from '@react-oauth/google';
import { PayloadAction, SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { selectRetailerId, selectRetailerSlug } from '@lib/core/retailers/selectors/retailer';
import { DESIGN_SET_WINE_EXPERIENCE, REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';
import {
  facebookLoginApiUrlCreator,
  googleLoginApiUrlCreator,
  loginApiUrlCreator,
  registrationApiUrlCreator,
  resetPasswordApiUrlCreator,
  setNewPasswordApiUrlCreator,
} from '@lib/core/users/slices/urls';
import { TUser } from '@lib/core/users/types';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import { removeSocialValuesLocalStorage } from '@lib/tools/shared/helpers/removeSocialValuesLocalStorage';
import { ISocialLoginFacebook } from '@lib/tools/views/web/interfaces/loginSocial';

export interface IRegisterParams {
  first_name?: string;
  last_name?: string;
  email?: string;
  password?: string;
}

interface ILoginRequestData {
  email: string;
  password: string;
  captchaValue: string;
  isCaptchaDisabled: boolean;
}

interface IAuthData {
  access_token: string;
  refresh_token: string;
}

export interface ILoginResponse extends IAuthData {
  user: TUser;
}

export interface IUserSlice {
  data: IAuthData;
  isLoading: boolean;
  error: SerializedError;
  rejectEndpointError: SerializedError;
  resetPasswordEmailSuccess: boolean;
}

const initialState: IUserSlice = {
  data: undefined,
  error: null,
  isLoading: false,
  rejectEndpointError: null,
  resetPasswordEmailSuccess: false,
};

export const actionSetNewPassword = createTypedAsyncThunk(
  'users/auth/actionSetNewPassword',
  async ({ password, token, uid }: { password: string; uid: string; token: string }) =>
    request({
      body: {
        new_password1: password,
        new_password2: password,
        token,
        uid,
      },
      method: REQUEST_METHODS.POST,
      url: setNewPasswordApiUrlCreator(),
    }),
);

export const actionResetPassword = createTypedAsyncThunk(
  'users/auth/actionResetPassword',
  async ({ email, sitekey }: { email: string; sitekey?: string }, { getState }) =>
    request({
      body: {
        design_set: DESIGN_SET_WINE_EXPERIENCE,
        email,
        retailer_slug: selectRetailerSlug(getState()),
        sitekey,
      },
      method: REQUEST_METHODS.POST,
      url: resetPasswordApiUrlCreator(),
    }),
);

export const actionLogin = createTypedAsyncThunk(
  'users/auth/actionLogin',
  async ({ email, password, captchaValue, isCaptchaDisabled }: ILoginRequestData) => {
    try {
      const loginData = await request({
        body: {
          email,
          password,
          sitekey: isCaptchaDisabled ? undefined : captchaValue,
        },
        method: REQUEST_METHODS.POST,
        url: loginApiUrlCreator(),
      });

      MixpanelTracker.events.userLogin();

      return loginData;
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

export const actionGoogleLogin = createTypedAsyncThunk(
  'users/auth/actionGoogleLogin',
  async ({ access_token }: TokenResponse, { getState }) => {
    if (!access_token) {
      return Promise.reject(parseError('Google auth API error: no token returned'));
    }
    removeSocialValuesLocalStorage();
    try {
      const loginData = await request({
        body: {
          access_token,
          retailer: selectRetailerId(getState()),
        },
        method: REQUEST_METHODS.POST,
        url: googleLoginApiUrlCreator(),
      });

      MixpanelTracker.events.userLogin();

      return loginData;
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

export const actionFacebookLogin = createTypedAsyncThunk(
  'users/auth/actionFacebookLogin',
  async ({ accessToken }: ISocialLoginFacebook, { getState }) => {
    if (!accessToken) {
      return Promise.reject(parseError('Facebook auth API error: no token returned'));
    }
    removeSocialValuesLocalStorage();
    try {
      const loginData = await request({
        body: {
          access_token: accessToken,
          retailer: selectRetailerId(getState()),
        },
        method: REQUEST_METHODS.POST,
        url: facebookLoginApiUrlCreator(),
      });

      MixpanelTracker.events.userLogin();

      return loginData;
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

export const actionRegister = createTypedAsyncThunk(
  'users/auth/actionRegister',
  async (requestData: IRegisterParams, { getState }) => {
    try {
      const newUserData = await request({
        body: {
          ...requestData,
          password2: requestData.password,
          retailer: selectRetailerId(getState()),
        },
        method: REQUEST_METHODS.POST,
        url: registrationApiUrlCreator(),
      });

      MixpanelTracker.events.userRegistrationCompleted();

      return newUserData;
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

export const googleLoginCallback = dispatch => response => dispatch(actionGoogleLogin(response));

export const facebookLoginCallback = dispatch => response => dispatch(actionFacebookLogin(response));

export const authSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(actionResetPassword.fulfilled, state => {
      state.isLoading = false;
      state.rejectEndpointError = null;
      state.resetPasswordEmailSuccess = true;
    });
    builder.addCase(actionResetPassword.rejected, (state, action) => {
      state.isLoading = false;
      state.rejectEndpointError = action.error;
      state.resetPasswordEmailSuccess = false;
    });
    builder.addMatcher(
      isAnyOf(actionLogin.pending, actionGoogleLogin.pending, actionFacebookLogin.pending, actionRegister.pending),
      state => {
        state.isLoading = true;
      },
    );
    builder.addMatcher(
      isAnyOf(
        actionLogin.fulfilled,
        actionGoogleLogin.fulfilled,
        actionFacebookLogin.fulfilled,
        actionRegister.fulfilled,
      ),
      (state, action: PayloadAction<IAuthData>) => {
        const { payload: { access_token, refresh_token } = {} } = action;
        state.data = {
          access_token,
          refresh_token,
        };
        state.isLoading = false;
      },
    );
    builder.addMatcher(
      isAnyOf(actionLogin.rejected, actionGoogleLogin.rejected, actionFacebookLogin.rejected, actionRegister.rejected),
      (state, action) => {
        state.isLoading = false;
        state.error = action.error;
      },
    );
  },
  initialState,
  name: 'auth',
  reducers: {
    actionResetAuthSlice: () => initialState,
    actionSaveAccessToken: (state, action: PayloadAction<string>) => {
      state.data = { ...state.data, access_token: action.payload };
    },
    resetAuthError: state => {
      state.error = null;
      state.rejectEndpointError = null;
    },
    resetResetPasswordState: state => {
      state.resetPasswordEmailSuccess = false;
    },
  },
});

export default authSlice.reducer;

export const { actionResetAuthSlice, actionSaveAccessToken, resetAuthError, resetResetPasswordState } =
  authSlice.actions;
